import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from "../api/apiClient";

export interface QuestionNotificationItem {
  programId: string;
  bookingId: string | null;
  programTitle: string;
  programIcon: string;
  pendingCount: number;
  unseenCount?: number;
  isSeen?: boolean;
  questions?: Array<{
    id: string;
    text: string;
    type: string;
    askedOn: string;
  }>;
}

export interface NotificationState {
  loading: boolean;
  unreadCount: number; // Count of newly unseen notifications
  totalPendingCount: number; // Total number of pending question items
  items: QuestionNotificationItem[];
  error: string | null;
  lastNotifiedCount: number;
}

const initialState: NotificationState = {
  loading: false,
  unreadCount: 0,
  totalPendingCount: 0,
  items: [],
  error: null,
  lastNotifiedCount: 0,
};

let activeNotificationPromise: Promise<any> | null = null;
let lastNotificationFetchTime = 0;

export const fetchUserNotifications = createAsyncThunk(
  "notifications/fetchUserNotifications",
  async (force: boolean | undefined, thunkAPI) => {
    if (activeNotificationPromise) {
      return activeNotificationPromise;
    }
    activeNotificationPromise = (async () => {
      try {
        const response = await apiClient.get("/api/v1/bookings/notifications");
        lastNotificationFetchTime = Date.now();
        return response.data.data;
      } catch (error: any) {
        return thunkAPI.rejectWithValue(
          error.message || "Failed to fetch notifications",
        );
      } finally {
        activeNotificationPromise = null;
      }
    })();
    return activeNotificationPromise;
  },
  {
    condition: (force, { getState }) => {
      if (force === true) return true;
      const state: any = getState();
      if (activeNotificationPromise) {
        return false;
      }
      if (Date.now() - lastNotificationFetchTime < 15000 && state.notifications?.items) {
        return false;
      }
      return true;
    },
  }
);

export const markNotificationsAsSeen = createAsyncThunk(
  "notifications/markNotificationsAsSeen",
  async (programId?: string) => {
    try {
      await apiClient.post("/api/v1/bookings/notifications/mark-seen", {
        programId,
      });
      return { programId };
    } catch (error: any) {
      return { programId };
    }
  }
);

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    clearNotifications: (state) => {
      state.unreadCount = 0;
      state.totalPendingCount = 0;
      state.items = [];
      state.lastNotifiedCount = 0;
    },
    setLastNotifiedCount: (state, action) => {
      state.lastNotifiedCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action) => {
        state.loading = false;
        const items = action.payload?.items || [];
        state.items = items;
        state.unreadCount = action.payload?.unreadCount !== undefined
          ? action.payload.unreadCount
          : items.filter((i: any) => !i.isSeen).length;
        state.totalPendingCount = action.payload?.totalPendingCount || items.length;
      })
      .addCase(fetchUserNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(markNotificationsAsSeen.fulfilled, (state, action) => {
        const programId = action.payload?.programId;
        if (programId) {
          state.items = state.items.map((item) =>
            item.programId === programId
              ? { ...item, isSeen: true, unseenCount: 0 }
              : item
          );
          state.unreadCount = state.items.filter((i) => !i.isSeen).length;
        } else {
          state.unreadCount = 0;
          state.items = state.items.map((item) => ({
            ...item,
            isSeen: true,
            unseenCount: 0,
          }));
        }
      })
      .addCase("user/logoutUser/fulfilled", (state) => {
        state.unreadCount = 0;
        state.totalPendingCount = 0;
        state.items = [];
        state.lastNotifiedCount = 0;
      });
  },
});

export const { clearNotifications, setLastNotifiedCount } =
  notificationSlice.actions;
export default notificationSlice.reducer;
